import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Api from '../utils/Api';
import { useAuth } from './AuthContext';
import { useError } from './ErrorContext';
import { useLoading } from './LoadingContext';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';

const PaymentContext = createContext();

export const usePayments = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayments must be used within a PaymentProvider');
  }
  return context;
};

export const PaymentProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { hideLoading } = useLoading();
  const { getToken } = useAuth();
  const { handleApiError } = useError();
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/payments');
   
  const getPayments = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
     
    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if (filters?.q) {
        query = '&q=' + filters?.q;
      } else {
        const q = searchParams.get('q') || '';
        query = '&q=' + q;
      }
      if (filters?.active) {
        query += '&active=' + filters?.active;
      } else {
        const active = searchParams.get('active') || '';
        query += '&active=' + active;
      }
 
 
      if (filters?.invoiceId) {
        query += '&invoiceId=' + filters?.invoiceId;
      } else {
        const invoiceId = searchParams.get('invoiceId') || searchParams.get('InvoiceId');
        if (invoiceId) {
          query += '&invoiceId=' + invoiceId;
        }
      }
  

      let response = await apiInstance.call('payments?page=' + page + '&paginate=' + paginate + '' + query, 'GET');

      if (filters?.all) {
        query = '';
        setPaginate(false);
        if (filters?.active) {
          query += '&active=' + filters?.active;
        }
        if (filters?.invoiceId) {
          query += '&invoiceId=' + filters?.invoiceId;
        }else {
          const invoiceId = searchParams.get('invoiceId') || searchParams.get('InvoiceId');
          if (invoiceId) {
            query += '&invoiceId=' + invoiceId;
          }
        }
 
        response = await apiInstance.call('payments?page=' + 0 + '' + query);
      }

      if (response.status === 200) {
        setPaginate(response.data.paginate);
        setPayments(response.data.payments || []);
        if (filters) {
          return response.data.payments;
        }
      } else {
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch payments information'
        });
      }
    } catch (error) {
      // silent
    } finally {
      // noop
    }
  }, [handleApiError, paginate, getToken]);

  const getPaymentByIdApi = useCallback(async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call(`payments/${id}/edit`, 'GET', null, true, false);
    if (response.status === 200) {
      let payments = { 'data': [response.data.payment] };
      setPayments(payments || []);
    }
  }, [getToken]);

  useEffect(() => {
    const initializeApi = async () => {
      try {
        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
          await getPayments(page);
        }
      } catch (error) {
        hideLoading();
      }
    };
    if (initialized) {
      initializeApi();
    }
  }, [getToken, getPayments, searchParams, initialized, hideLoading]);

  const getPaymentById = useCallback(async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    try {
      const response = await apiInstance.call(`payments/${id}/edit`, 'GET', null, true);
      if (response.status === 200) {
        return { status: response.status, data: response.data.payment };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to fetch payment details'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to fetch payment details'
      });
      return { status: 500, error: error.message };
    }
  }, [getToken, handleApiError]);

  const addPayment = async (data) => {
    setIsLoading(true);
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      setIsLoading(false);
      return;
    }
    try {
      let formData = new FormData();
      const dataCopy = { ...data };
      formData.append('data', JSON.stringify(dataCopy));
      const response = await apiInstance.call('payments', 'POST', formData, true, true);
      if (response.status === 201 || response.status === 200) {
        await getPayments();
        setIsLoading(false);
        return { status: response.status, data: response.data };
      } else {
        setIsLoading(false);
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add Payment'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      setIsLoading(false);
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to create payment'
      });
      return { status: 500, error: error.message };
    }
  };

  const updatePayment = async (id, updatedData) => {
    setIsLoading(true);
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      setIsLoading(false);
      return;
    }
    try {
      let formData = new FormData();
      const dataCopy = { ...updatedData };
      formData.append('data', JSON.stringify(dataCopy));
      formData.append('_method', 'PUT');
      const response = await apiInstance.call(`payments/${id}`, 'POST', formData, true, true);
      if (response.status === 200) {
        await getPayments();
        setIsLoading(false);
        return { status: response.status, data: response.data };
      } else {
        setIsLoading(false);
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update payment'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      setIsLoading(false);
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to update payment'
      });
      return { status: 500, error: error.message };
    }
  };

  const deletePayment = async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    try {
      const response = await apiInstance.call(`payments/${id}`, 'DELETE', null, true);
      if (response.status === 200) {
        await getPayments();
        return { status: response.status, data: response.data };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to delete payment'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete payment'
      });
      return { status: 500, error: error.message };
    }
  };

  const value = {
    payments,
    getPayments,
    getPaymentById,
    addPayment,
    getPaymentByIdApi,
    createPayment: addPayment,
    updatePayment,
    deletePayment,
    isLoading
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
};

export default PaymentContext;


